import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import { Folder, FileText, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function GoogleDriveSelector({ open, onOpenChange, sourceConnId, onSelect }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentFolder, setCurrentFolder] = useState(null); // null = root
    const [folderHistory, setFolderHistory] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]); // Array of { id, name, type }

    useEffect(() => {
        if (open && sourceConnId) {
            loadFiles(null);
            setSelectedItems([]); // Reset selection on open
        }
    }, [open, sourceConnId]);

    const loadFiles = async (folderId) => {
        setLoading(true);
        try {
            const params = { connection_id: sourceConnId };
            if (folderId) {
                params.parent_id = folderId;
            }

            console.log('Loading Google Drive files with params:', params);
            const response = await api.get('/google/files', { params });
            console.log('Google Drive files response:', response.data);
            setFiles(response.data.files || []);
        } catch (error) {
            console.error('Failed to load Drive files:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to load Google Drive files';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleFolderClick = (folder) => {
        setFolderHistory([...folderHistory, currentFolder]);
        setCurrentFolder(folder);
        loadFiles(folder.id);
    };

    const handleBack = () => {
        const newHistory = [...folderHistory];
        const prevFolder = newHistory.pop();
        setFolderHistory(newHistory);
        setCurrentFolder(prevFolder);
        loadFiles(prevFolder ? prevFolder.id : null);
    };

    const handleToggleSelect = (file, e) => {
        if (e) e.stopPropagation();

        const isSelected = selectedItems.some(item => item.id === file.id);
        const type = file.mimeType === 'application/vnd.google-apps.folder' ? 'folder' : 'file';

        if (isSelected) {
            setSelectedItems(selectedItems.filter(item => item.id !== file.id));
        } else {
            setSelectedItems([...selectedItems, {
                id: file.id,
                name: file.name,
                type: type,
                filter_value: file.id,
                filter_label: file.name
            }]);
        }
    };

    const handleConfirm = () => {
        onSelect(selectedItems);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {currentFolder && (
                            <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        )}
                        {currentFolder ? currentFolder.name : 'Google Drive'}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-hidden min-h-[300px]">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <ScrollArea className="h-full">
                            <div className="space-y-1 p-2">
                                {files.length === 0 ? (
                                    <div className="text-center text-muted-foreground py-8">
                                        No files found
                                    </div>
                                ) : (
                                    files.map((file) => {
                                        const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
                                        const isSelected = selectedItems.some(item => item.id === file.id);

                                        return (
                                            <div
                                                key={file.id}
                                                className={`flex items-center justify-between p-2 rounded-md cursor-pointer group ${isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                                                onClick={() => isFolder ? handleFolderClick(file) : handleToggleSelect(file)}
                                            >
                                                <div className="flex items-center gap-3 overflow-hidden flex-1">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() => handleToggleSelect(file)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    {isFolder ? (
                                                        <Folder className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                                    ) : (
                                                        <FileText className="h-5 w-5 text-blue-400 flex-shrink-0" />
                                                    )}
                                                    <span className="truncate">{file.name}</span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    {isFolder && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </ScrollArea>
                    )}
                </div>

                <DialogFooter className="flex justify-between items-center sm:justify-between">
                    <div className="text-sm text-muted-foreground">
                        {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                    </div>
                    <Button onClick={handleConfirm} disabled={selectedItems.length === 0}>
                        Add Selected
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
